/**
 * Where a signed-in session lands when the server placed it nowhere.
 *
 * `inferActiveTenant` (`@oip/domain`) returns nothing for THREE reasons — no
 * memberships, every one suspended or archived, or **more than one usable**,
 * where it declines to guess. Declining is right: landing somebody in an
 * arbitrary organisation is worse than asking, because they may act on what
 * they see before noticing where they are.
 *
 * `RequireAuth` used to collapse all three into `NoUsableTenantPage`, so a
 * person reaching a dozen organisations was told they did not belong to one.
 *
 * Its own module because the failure is INVISIBLE: the wrong page renders
 * perfectly, and only somebody in that exact situation would notice. Three
 * lines, testable as a decision rather than through a rendered tree.
 */

/** Only the field the decision reads. Keeps this free of the auth context. */
export interface LandingChoice {
  state: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

export type Landing =
  /** Render the product. */
  | 'APP'
  /** Several places to go; ask which. */
  | 'CHOOSE'
  /** Send them to their own white-label console. */
  | 'PARTNER'
  /** Nowhere to go; explain which kind of nowhere. */
  | 'NONE';

export function landingFor(input: {
  tenantId: string | null;
  isPlatformAdmin: boolean;
  choices: readonly LandingChoice[];
  /**
   * Whether the session administers a white-label partner.
   *
   * A FOURTH reason to have no tenant, and the one this rule did not have to
   * cover until the partner console existed: a partner administrator who has not
   * created a company yet holds no membership anywhere, and every place they
   * could go is a place they have to create first.
   */
  administersWhiteLabel?: boolean;
}): Landing {
  if (input.tenantId !== null) return 'APP';
  // A platform administrator legitimately has no tenant — that is how they
  // reach the platform screens.
  if (input.isPlatformAdmin) return 'APP';
  /*
   * A partner administrator has a HOME, and it is not a customer.
   *
   * This REFINES design D13, which said they reach `ChooseTenantPage`. D13 was
   * written before the partner console existed, and its actual concern — "a
   * partner administrator must not LAND on a partner" — is about not being
   * placed inside an arbitrary customer. The console is not a customer: it is
   * their own scope, it lists every company they administer, and entering one
   * is then a visible act rather than a default.
   *
   * Both alternatives were driven and both are worse. With no companies, the
   * chooser is empty and they were told "you do not belong to a tenant yet" —
   * on the screen whose whole purpose is creating the tenant they were being
   * told they lacked. With companies, creating one bounced them out of the
   * console into a chooser the moment the session refreshed.
   *
   * Checked BEFORE `choices`, which is what makes both of those go away.
   */
  if (input.administersWhiteLabel === true) return 'PARTNER';
  /*
   * ENTERABLE, not merely listed. `setTenant` refuses anything that is not
   * ACTIVE, so offering a suspended organisation would be a button that does
   * nothing — the door into a room with no floor the switcher already avoids.
   */
  if (input.choices.some((c) => c.state === 'ACTIVE')) return 'CHOOSE';
  return 'NONE';
}

// ─────────────────────── where a session lands ───────────────────────

/** The fields the landing rule reads from a membership. */
export interface LandingMembership {
  tenantId: string;
  tenantType: string;
  tenantState: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
}

/**
 * The client's copy of `inferActiveTenant` (`@oip/domain`), and it must MATCH.
 *
 * A copy exists because `@oip/contracts` compiles to CommonJS, so the web app
 * takes its types and not its runtime values (risk R9) — the same arrangement
 * `telemetryOptions.ts` and `outcomeOptions.ts` use. The domain function cannot
 * be imported here.
 *
 * **This copy had DIVERGED**, and the divergence was silent. It ended:
 *
 * ```ts
 * usable.find((m) => m.tenantType === 'SUB')?.tenantId ?? usable[0]?.tenantId ?? null
 * ```
 *
 * — so where the server declined to guess between several organisations, the
 * client picked the FIRST one. A white-label partner administrator, a member of
 * every company they look after, was dropped into an arbitrary customer with
 * nothing on screen saying a choice had been made for them. They could act on
 * what they saw believing it was the whole picture, which is precisely the harm
 * `inferActiveTenant` declines to cause.
 *
 * The same class of defect the hierarchy work already found once: *"they used
 * to carry separate copies and had already diverged, so the guard placed a
 * session in a tenant while `me` told the client there was none."*
 *
 * Returning null is not a failure. It means ASK — see {@link landingFor}.
 */
export function preferredTenant(input: {
  memberships: readonly LandingMembership[];
  /** A remembered choice, honoured only if it is still reachable and active. */
  saved: string | null;
  /** Branches reachable by derived role rather than by membership. */
  derived: readonly { tenantId: string; tenantState: string }[];
}): string | null {
  const usable = input.memberships.filter((m) => m.tenantState === 'ACTIVE');

  const reachable =
    input.saved !== null &&
    (usable.some((m) => m.tenantId === input.saved) ||
      input.derived.some((d) => d.tenantId === input.saved && d.tenantState === 'ACTIVE'));
  if (reachable) return input.saved;

  // From here, exactly `inferActiveTenant`: the sole operational membership,
  // else the sole membership, else nothing.
  const operational = usable.filter((m) => m.tenantType !== 'PARENT');
  if (operational.length === 1) return operational[0]!.tenantId;
  if (usable.length === 1) return usable[0]!.tenantId;
  return null;
}
